import { Typography } from '@mui/material';
import { GridApiRef, GridCellParams, GridRowId } from '@mui/x-data-grid-pro';

interface EditableCellProps {
  className: string;
  apiRef: GridApiRef;
  editable?: boolean;
  highlightField?: boolean;
}

type Direction = 'next' | 'prev';

function findNextEditableCell(
  api: GridApiRef,
  currentRowId: GridRowId,
  currentField: string,
  direction: Direction,
): { id: string | number; field: string } | null {
  const visibleColumns = api.current
    .getAllColumns()
    .filter((col) => !col.hide && col.field !== '__check__');

  const editableFields = visibleColumns.map((col) => col.field);
  const currentColIndex = editableFields.indexOf(currentField);

  const rowIds = api.current.getAllRowIds() as GridRowId[];
  const currentRowIndex = rowIds.indexOf(currentRowId);

  const rowStep = direction === 'next' ? 1 : -1;
  const colStep = direction === 'next' ? 1 : -1;

  let rowIndex = currentRowIndex;
  let colIndex = currentColIndex + colStep;

  while (rowIndex >= 0 && rowIndex < rowIds.length) {
    const rowId = rowIds[rowIndex];

    //Reset column index when changing row
    if (rowIndex !== currentRowIndex) {
      colIndex = direction === 'next' ? 0 : editableFields.length - 1;
    }

    while (colIndex >= 0 && colIndex < editableFields.length) {
      const field = editableFields[colIndex];
      const cellParams = getCellParams(api, rowId, field);
      if (cellParams && api.current.isCellEditable(cellParams)) {
        return { id: rowId, field };
      }
      colIndex += colStep;
    }
    rowIndex += rowStep;
  }

  return null;
}

function getCellParams(
  api: GridApiRef,
  id: GridRowId,
  field: string,
): GridCellParams | null {
  const row = api.current.getRow(id);
  const colDef = api.current.getColumn(field);
  if (!row || !colDef) {
    return null;
  }

  return {
    id,
    field,
    value: row[field],
    row,
    colDef,
    getValue: api.current.getCellValue,
    rowNode: api.current.getRowNode(id)!,
    cellMode: api.current.getCellMode(id, field),
    hasFocus: false,
    tabIndex: -1,
    formattedValue: api.current.getCellValue(id, field),
  };
}

export function EditableCell(props: EditableCellProps & GridCellParams) {
  const {
    value,
    className,
    id,
    field,
    apiRef,
    editable = true,
    highlightField = false,
  } = props;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();

      const direction = e.shiftKey ? 'prev' : 'next';
      const nextCell = findNextEditableCell(apiRef, id, field, direction);

      if (nextCell && apiRef.current) {
        apiRef.current.setCellMode(id, field, 'view');
        apiRef.current.setCellFocus(nextCell.id, nextCell.field);
        //apiRef.current.setCellMode(nextCell.id, nextCell.field, 'edit');
        const rowIndex = apiRef.current.getAllRowIds().indexOf(nextCell.id);
        const colIndex = apiRef.current
          .getAllColumns()
          .findIndex((col) => col.field === nextCell.field);

        apiRef.current.scrollToIndexes({ rowIndex, colIndex });
      }
    }
  };

  return (
    <div
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        backgroundColor: highlightField && editable ? '#e3dddd' : '',
        textAlign: 'left',
        paddingLeft: '5px',
        height: '70%',
        cursor: editable ? 'pointer' : 'default',
      }}
      className={className}
      onClick={() => {
        if (editable) {
          apiRef.current.setCellMode(id, field, 'edit');
        }
      }}
    >
      <Typography title={value}>{value}</Typography>
    </div>
  );
}
